import type { Dispatch } from 'redux';
import { setIn } from 'timm';
import { call } from 'typed-redux-saga';
import { v4 as uuid } from 'uuid';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  setupSlice,
} from '@owl-frontend/redux';
import { createLogger } from '@owl-lib/logger';
import chatApiClient from '../../api/chat/client';
import { globalBus } from '../../context/event-bus';
import { ChatMessageType, type ChatPayload } from './interface';

const logger = createLogger(__filename);
export enum CHAT_EVENTS {
  ChatStatus = 'chat_status',
}

export const TIMEOUT_MS = 300_000;
const END_OF_MESSAGE = 'END_OF_MESSAGE';
export interface ChatState {
  chatMessages: {
    [dossier_id: string]: any;
  };
  templateVersions: {
    [dossier_id: string]: {
      [template_id: string]: Array<{
        html: string;
        template_version_id: string;
        created_at: string;
      }>;
    };
  };
  chatThreads: {
    [dossier_id: string]: {
      [template_id: string]: string;
    };
  };
  actions: {
    chat: AsyncActionState;
  };
}

const initialState: ChatState = {
  chatMessages: {},
  templateVersions: {},
  chatThreads: {},
  actions: {
    chat: { status: 'uninitialized' },
  },
};

function* chat(action: {
  payload: {
    dossierId: string;
    templateId: string;
    message: string;
    selectedText: string;
    xpaths: string[];
    threadId: string;
    dispatch: Dispatch<any>;
  };
}) {
  const {
    dossierId,
    templateId,
    message,
    selectedText,
    xpaths,
    threadId,
    dispatch,
  } = action.payload;
  const socket: WebSocket = yield* call(chatApiClient.chat);
  dispatch({
    type: 'chat/onChatMessages',
    payload: {
      dossierId,
      templateId,
      status: 'in_progress',
      message: {
        type: ChatMessageType.User,
        data: selectedText ? `${message}\n\n"${selectedText}"` : message,
        request_id: null,
        seq: null,
      },
    },
  });
  socket.onopen = () => {
    socket.send(
      JSON.stringify({
        message,
        selected_text: selectedText,
        context: {
          claim_ids: [dossierId],
          template_ids: [templateId],
          xpaths,
          thread_id: threadId,
        },
        request_id: uuid(),
      })
    );
    globalBus.emit(CHAT_EVENTS.ChatStatus, 'in_progress');
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data);
    dispatch({
      type: 'chat/onChatMessages',
      payload: {
        dossierId,
        templateId,
        status: data.status,
        message: data.message,
      },
    });
  };

  socket.onerror = (error) => {
    logger.error('WebSocket Error:', error);
    globalBus.emit(CHAT_EVENTS.ChatStatus, null);
  };

  socket.onclose = () => {
    logger.info('WebSocket closed from chat.');
    globalBus.emit(CHAT_EVENTS.ChatStatus, null);
  };
}

const getMessageType = (
  message: {
    content: Array<{ type: string; text: string }>;
    metadata?: {
      updated?: boolean;
    };
    role: string;
  },
  idx: number
) => {
  const isLastEntry = idx === message.content.length - 1;
  const isUpdated = message.metadata?.updated === true;

  if (message.role === 'user') {
    return ChatMessageType.User;
  } else if (isLastEntry && isUpdated) {
    return ChatMessageType.Chunk;
  } else {
    return ChatMessageType.ThoughtChunk;
  }
};

const formatUserMessage = (message: {
  content: Array<{ type: string; text: string }>;
  metadata?: { selected_text?: string; updated?: boolean };
}) => {
  if (message.metadata?.selected_text) {
    return `${message.content.at(-1)!.text}\n\n"${
      message.metadata.selected_text
    }"`;
  }
  return message.content.at(-1)!.text;
};

const slice = setupSlice('chat', initialState)
  .addAsyncSagas(
    {
      chat,
    },
    { timeoutLimit: TIMEOUT_MS }
  )
  .addReducers({
    onChatMessages: (
      state,
      action: {
        payload: ChatPayload;
      }
    ) => {
      const { dossierId, templateId, status, message } = action.payload;
      const { type } = message;

      const existing = state.chatMessages[dossierId] ?? {
        messages: [],
        buffer: null,
      };

      const saveMessage = () => {
        state.chatMessages = setIn(
          state.chatMessages,
          [dossierId],
          existing
        ) as typeof state.chatMessages;
      };

      switch (type) {
        case ChatMessageType.TemplateChunk: {
          const existingVersions =
            state.templateVersions?.[dossierId]?.[templateId] ?? [];

          state.templateVersions = setIn(
            state.templateVersions,
            [dossierId, templateId],
            [...existingVersions, message.template_version]
          ) as typeof state.templateVersions;

          const msgs = existing.messages;
          const last = msgs.at(-1);

          if (last && last.message_type !== ChatMessageType.User) {
            last.message_type = 'chunk';
          }
          return;
        }
        case ChatMessageType.User: {
          existing.messages.push({
            message_type: ChatMessageType.User,
            message: message.data,
          });

          return saveMessage();
        }

        default: {
          if (message.data === END_OF_MESSAGE) {
            if (existing.buffer) {
              existing.messages.push({
                message_type: ChatMessageType.ThoughtChunk,
                message: existing.buffer.content,
              });
            }
            existing.buffer = null;
            return saveMessage();
          }
          if (status !== 'done') {
            //TODO: Clean up once backend fixes are available.
            if (message.data.includes(END_OF_MESSAGE)) {
              const parts = message.data.split(END_OF_MESSAGE);
              const firstMessage = parts[0].trimEnd();
              if (existing.buffer) {
                existing.buffer.content += firstMessage;
                existing.messages.push({
                  message_type: ChatMessageType.ThoughtChunk,
                  message: existing.buffer.content,
                });
              } else if (firstMessage) {
                existing.messages.push({
                  message_type: ChatMessageType.ThoughtChunk,
                  message: firstMessage,
                });
              }
              existing.buffer = null;
              for (const part of parts.slice(1, -1)) {
                if (part.trim()) {
                  existing.messages.push({
                    message_type: ChatMessageType.ThoughtChunk,
                    message: part.trim(),
                  });
                }
              }
              const last = parts.at(-1) ?? '';
              if (last) {
                existing.buffer = { type, content: last };
              }
              return saveMessage();
            }
            if (!existing.buffer) {
              existing.buffer = { type, content: message.data };
            } else {
              existing.buffer.content += message.data;
            }
          }
          return saveMessage();
        }
      }
    },
    clearChatMessages: (
      state,
      action: {
        payload: { dossierId: string };
      }
    ) => {
      const { dossierId } = action.payload;

      state.chatMessages = setIn(state.chatMessages, [dossierId], {
        messages: [],
        buffer: null,
      }) as typeof state.chatMessages;
    },
    setChatMessages: (
      state,
      action: {
        payload: {
          dossierId: string;
          messages: Array<{
            role: string;
            content: Array<{ type: string; text: string }>;
            metadata?: {
              selected_text?: string;
              updated?: boolean;
            };
          }>;
        };
      }
    ) => {
      const { dossierId, messages } = action.payload;
      const formattedMessages = messages.flatMap((message) => {
        if (message.role === 'user') {
          if (message.content.length === 0) {
            return [];
          }

          return [
            {
              message_type: getMessageType(message, 0),
              message: formatUserMessage(message),
            },
          ];
        }

        return message.content.map((content, idx) => ({
          message_type: getMessageType(message, idx),
          message: content.text,
        }));
      });

      state.chatMessages = setIn(state.chatMessages, [dossierId], {
        messages: formattedMessages,
        buffer: null,
      }) as typeof state.chatMessages;
    },
    setTemplateVersions: (
      state,
      action: {
        payload: {
          dossierId: string;
          templateId: string;
          templateVersions: {
            html: string;
            template_version_id: string;
            created_at: string;
          }[];
          overwrite?: boolean;
        };
      }
    ) => {
      const { dossierId, templateId, templateVersions, overwrite } =
        action.payload;

      const existingVersions = overwrite
        ? []
        : state.templateVersions?.[dossierId]?.[templateId] ?? [];

      state.templateVersions = setIn(
        state.templateVersions,
        [dossierId, templateId],
        [...existingVersions, ...templateVersions]
      ) as typeof state.templateVersions;
    },
    setChatThread: (
      state,
      action: {
        payload: {
          dossierId: string;
          templateId: string;
          threadId: string;
        };
      }
    ) => {
      const { dossierId, templateId, threadId } = action.payload;
      state.chatThreads = setIn(
        state.chatThreads,
        [dossierId, templateId],
        threadId
      ) as typeof state.chatThreads;
    },
    clearChatThread: (
      state,
      action: {
        payload: { dossierId: string; templateId: string };
      }
    ) => {
      const { dossierId, templateId } = action.payload;

      const existing = state.chatThreads[dossierId];
      if (!existing) {
        return;
      }

      const updated = { ...existing };
      delete updated[templateId];

      state.chatThreads = setIn(
        state.chatThreads,
        [dossierId],
        updated
      ) as typeof state.chatThreads;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
